import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { TextPlugin } from 'gsap/TextPlugin';
import './home.css';
import dateIcon from '../asserts/dd.png';
import locationIcon from '../asserts/ll.png';
import hoursIcon from '../asserts/hh.png';
import asterisk from '../asserts/as.png';
import Footer from '../components/layout/footer';
import Loader from '../components/layout/preloader';
import Popad from '../components/layout/popad';
import Label from '../components/common/label';
import Header from '../components/layout/header';
import ticketImg from '../asserts/tic.png';
import { Link } from 'react-router-dom';
import Btn2 from '../components/common/btn2';
import TalkCard from '../components/common/TalkCard';
import ScrollVelocity from '../components/layout/ScrollVelocity';
import { supabase } from '../supabaseClient';

gsap.registerPlugin(ScrollTrigger, TextPlugin);

const splitChars = (text) =>
  text.split('').map((ch, i) => (
    <span key={i} className="gsap-char">
      {ch === ' ' ? '\u00A0' : ch}
    </span>
  ));

const iconMap = { DATES: dateIcon, LOCATION: locationIcon, HOURS: hoursIcon };

const Home = () => {
  const [heroData, setHeroData] = useState({});
  const [exhibitionInfo, setExhibitionInfo] = useState([]);
  const [talks, setTalks] = useState([]);
  const [about, setAbout] = useState({});
  const [aboutStats, setAboutStats] = useState([]);
  const [ticket, setTicket] = useState({});
  const [ticketPoints, setTicketPoints] = useState([]);

  useEffect(() => {
    const load = async () => {
      const [hero, info, tlks, abt, stats, tkt, pts] = await Promise.all([
        supabase.from('landing_page_hero').select('*'),
        supabase.from('exhibition_info').select('*').order('display_order'),
        supabase.from('design_talks').select('*'),
        supabase.from('about_event_static').select('*'),
        supabase.from('about_event_stats').select('*'),
        supabase.from('ticketing_cta_static').select('*'),
        supabase.from('ticketing_cta_points').select('*'),
      ]);
      setHeroData(hero.data?.[0] || {});
      setExhibitionInfo(info.data || []);
      setTalks(tlks.data || []);
      setAbout(abt.data?.[0] || {});
      setAboutStats(stats.data || []);
      setTicket(tkt.data?.[0] || {});
      setTicketPoints(pts.data || []);
    };
    load();
  }, []);

  const heroImgRef = useRef();
  const titleBoxRef = useRef();
  const asteriskRef = useRef();
  const textBoxRef = useRef();
  const labelRef = useRef();
  const strip1Ref = useRef();
  const strip2Ref = useRef();
  const strip3Ref = useRef();
  const aboutRef = useRef();
  const aboutImgRef = useRef();
  const aboutLabelRef = useRef();
  const aboutTitleRef = useRef();
  const aboutTextRef = useRef();
  const stat1Ref = useRef();
  const stat2Ref = useRef();
  const stat3Ref = useRef();
  const talksRef = useRef();
  const talksTitleRef = useRef();
  const card1Ref = useRef();
  const card2Ref = useRef();
  const card3Ref = useRef();
  const ticketRef = useRef();
  const ticketImgRef = useRef();
  const ticketTitleRef = useRef();
  const point1Ref = useRef();
  const point2Ref = useRef();
  const point3Ref = useRef();
  const ticketBtnsRef = useRef();

  const stripRefs = [strip1Ref, strip2Ref, strip3Ref];
  const statRefs = [stat1Ref, stat2Ref, stat3Ref];
  const cardRefs = [card1Ref, card2Ref, card3Ref];
  const pointRefs = [point1Ref, point2Ref, point3Ref];

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ delay: 5.2 });

      tl.fromTo(heroImgRef.current,
        { clipPath: 'inset(100% 0% 0% 0%)', opacity: 0 },
        { clipPath: 'inset(0% 0% 0% 0%)', opacity: 1, duration: 1.2, ease: 'expo.out' }
      )
        .fromTo(labelRef.current, { opacity: 0 }, {
          duration: 0.6, ease: 'none',
          keyframes: [
            { opacity: 0, duration: 0.05 }, { opacity: 1, duration: 0.05 },
            { opacity: 0, duration: 0.05 }, { opacity: 0.4, duration: 0.05 },
            { opacity: 0, duration: 0.05 }, { opacity: 1, duration: 0.35 },
          ]
        }, '-=0.6')
        .fromTo(titleBoxRef.current,
          { x: -120, opacity: 0, skewX: -8 },
          { x: 0, opacity: 1, skewX: 0, duration: 0.9, ease: 'expo.out' }, '-=0.3'
        )
        .fromTo('.gsap-char',
          { y: 60, opacity: 0, rotateX: -90 },
          { y: 0, opacity: 1, rotateX: 0, duration: 0.7, stagger: 0.04, ease: 'back.out(1.7)' }, '-=0.6'
        )
        .fromTo(asteriskRef.current,
          { scale: 0, rotation: -180, opacity: 0 },
          { scale: 1, rotation: 0, opacity: 1, duration: 1, ease: 'elastic.out(1, 0.5)' }, '-=0.8'
        )
        .fromTo(textBoxRef.current,
          { x: 80, opacity: 0 },
          { x: 0, opacity: 1, duration: 0.8, ease: 'power3.out' }, '-=0.5'
        );

      stripRefs.forEach((r) => {
        tl.fromTo(r.current,
          { y: 50, opacity: 0, filter: 'blur(8px)' },
          { y: 0, opacity: 1, filter: 'blur(0px)', duration: 0.6, ease: 'power2.out' }, '-=0.4'
        );
      });

      gsap.to(asteriskRef.current, { rotation: 360, duration: 12, repeat: -1, ease: 'none', delay: 5.5 });

      const onMouseMove = (e) => {
        if (!asteriskRef.current) return;
        const rect = asteriskRef.current.getBoundingClientRect();
        gsap.to(asteriskRef.current, {
          x: (e.clientX - rect.left - rect.width / 2) * 0.18,
          y: (e.clientY - rect.top - rect.height / 2) * 0.18,
          duration: 0.5, ease: 'power2.out',
        });
      };
      window.addEventListener('mousemove', onMouseMove);
      heroImgRef.current?.addEventListener('mouseleave', () =>
        gsap.to(asteriskRef.current, { x: 0, y: 0, duration: 0.8, ease: 'elastic.out(1, 0.4)' })
      );

      gsap.fromTo(aboutImgRef.current,
        { clipPath: 'inset(0% 100% 0% 0%)', x: 60 },
        {
          clipPath: 'inset(0% 0% 0% 0%)', x: 0, duration: 1.3, ease: 'expo.out',
          scrollTrigger: { trigger: aboutRef.current, start: 'top 75%' }
        }
      );
      gsap.fromTo(aboutLabelRef.current, { opacity: 0, y: 20 },
        {
          opacity: 1, y: 0, duration: 0.7, ease: 'power3.out',
          scrollTrigger: { trigger: aboutRef.current, start: 'top 72%' }
        }
      );
      gsap.fromTo(aboutTitleRef.current, { opacity: 0, x: -50, skewX: -5 },
        {
          opacity: 1, x: 0, skewX: 0, duration: 1, ease: 'expo.out',
          scrollTrigger: { trigger: aboutRef.current, start: 'top 68%' }
        }
      );
      gsap.fromTo(aboutTextRef.current, { opacity: 0, y: 30, filter: 'blur(6px)' },
        {
          opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.9, ease: 'power2.out',
          scrollTrigger: { trigger: aboutRef.current, start: 'top 65%' }
        }
      );
      statRefs.forEach((r, i) => {
        gsap.fromTo(r.current, { scale: 0.4, opacity: 0, y: 30 },
          {
            scale: 1, opacity: 1, y: 0, duration: 0.7, ease: 'back.out(1.8)', delay: i * 0.15,
            scrollTrigger: { trigger: aboutRef.current, start: 'top 60%' }
          }
        );
      });

      gsap.fromTo(talksTitleRef.current,
        { opacity: 0, letterSpacing: '20px', filter: 'blur(10px)' },
        {
          opacity: 1, letterSpacing: '2.294px', filter: 'blur(0px)', duration: 1.1, ease: 'power3.out',
          scrollTrigger: { trigger: talksRef.current, start: 'top 75%' }
        }
      );

      gsap.fromTo(ticketImgRef.current,
        { y: -120, opacity: 0, rotation: -15, scale: 0.7 },
        {
          y: 0, opacity: 1, rotation: 0, scale: 1, duration: 1.3, ease: 'elastic.out(1, 0.5)',
          scrollTrigger: { trigger: ticketRef.current, start: 'top 72%' }
        }
      );
      gsap.fromTo(ticketTitleRef.current, { opacity: 0, x: 60, filter: 'blur(8px)' },
        {
          opacity: 1, x: 0, filter: 'blur(0px)', duration: 1, ease: 'expo.out',
          scrollTrigger: { trigger: ticketRef.current, start: 'top 68%' }
        }
      );
      pointRefs.forEach((r, i) => {
        gsap.fromTo(r.current, { x: 80, opacity: 0 },
          {
            x: 0, opacity: 1, duration: 0.7, ease: 'power3.out', delay: i * 0.18,
            scrollTrigger: { trigger: ticketRef.current, start: 'top 60%' }
          }
        );
      });
      gsap.fromTo(ticketBtnsRef.current, { scale: 0.7, opacity: 0, y: 20 },
        {
          scale: 1, opacity: 1, y: 0, duration: 0.7, ease: 'back.out(2)',
          scrollTrigger: { trigger: ticketRef.current, start: 'top 55%' }
        }
      );
      gsap.to(ticketImgRef.current, {
        y: -60, ease: 'none',
        scrollTrigger: { trigger: ticketRef.current, start: 'top bottom', end: 'bottom top', scrub: 1.5 },
      });

      return () => window.removeEventListener('mousemove', onMouseMove);
    });

    return () => ctx.revert();
  }, []);

  useEffect(() => {
    if (!talks.length) return;

    const cardFrom = [
      { x: -150, y: 80, rotation: -12 },
      { x: 0, y: 120, rotation: 6 },
      { x: 150, y: 80, rotation: 12 },
    ];

    const cleanups = cardRefs.map((r, i) => {
      const el = r.current;
      if (!el) return null;

      gsap.fromTo(el,
        { ...cardFrom[i], opacity: 0, scale: 0.75 },
        {
          x: 0, y: 0, rotation: 0, opacity: 1, scale: 1, duration: 1, ease: 'expo.out', delay: i * 0.12,
          scrollTrigger: { trigger: talksRef.current, start: 'top 70%' }
        }
      );

      const onMove = (e) => {
        const rect = el.getBoundingClientRect();
        gsap.to(el, {
          rotateX: ((e.clientY - rect.top) / rect.height - 0.5) * 20,
          rotateY: ((e.clientX - rect.left) / rect.width - 0.5) * -20,
          scale: 1.04, duration: 0.3, ease: 'power2.out', transformPerspective: 800,
        });
      };
      const onLeave = () =>
        gsap.to(el, { rotateX: 0, rotateY: 0, scale: 1, duration: 0.5, ease: 'elastic.out(1, 0.5)' });

      el.addEventListener('mousemove', onMove);
      el.addEventListener('mouseleave', onLeave);
      return () => {
        el.removeEventListener('mousemove', onMove);
        el.removeEventListener('mouseleave', onLeave);
      };
    });

    return () => cleanups.forEach((fn) => fn && fn());
  }, [talks]);

  const pointIcons = [
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
      <circle cx="11" cy="11" r="4" stroke="var(--color-primary)" strokeWidth="1.5" />
      <circle cx="11" cy="11" r="9.5" stroke="var(--color-primary)" strokeWidth="1" />
      <line x1="11" y1="1" x2="11" y2="4" stroke="var(--color-primary)" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="11" y1="18" x2="11" y2="21" stroke="var(--color-primary)" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="21" y1="11" x2="18" y2="11" stroke="var(--color-primary)" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="4" y1="11" x2="1" y2="11" stroke="var(--color-primary)" strokeWidth="1.5" strokeLinecap="round" />
    </svg>,
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
      <path d="M11 2C7.686 2 5 4.686 5 8c0 4.5 6 12 6 12s6-7.5 6-12c0-3.314-2.686-6-6-6z" stroke="var(--color-primary)" strokeWidth="1.5" strokeLinejoin="round" />
      <circle cx="11" cy="8" r="2" stroke="var(--color-primary)" strokeWidth="1.5" />
    </svg>,
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
      <circle cx="7" cy="8" r="3" stroke="var(--color-primary)" strokeWidth="1.5" />
      <circle cx="15" cy="8" r="3" stroke="var(--color-primary)" strokeWidth="1.5" />
      <path d="M1 18c0-3.314 2.686-5 6-5" stroke="var(--color-primary)" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M21 18c0-3.314-2.686-5-6-5" stroke="var(--color-primary)" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M7.5 13.2C8.3 13.07 9.13 13 10 13h2c.87 0 1.7.07 2.5.2C16.93 13.6 19 15.07 19 18H3c0-2.93 2.07-4.4 4.5-4.8z" stroke="var(--color-primary)" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>,
  ];

  return (
    <>
      <Popad />
      <Loader />
      <Header />
      <main>
        <section className="hero">
          <div className="hero__image-wrap" ref={heroImgRef}>
            <div className="hero__label-wrap" ref={labelRef}>
              <Label labelt={heroData.subtitle_en} />
            </div>
            <div className="hero__title-box title-box" ref={titleBoxRef}>
              <img src={asterisk} alt="Decorative asterisk" className="asterk" ref={asteriskRef} />
              <div className="hero__title-content">
                <h1 className="h-title">
                  {heroData.title_en && splitChars(heroData.title_en.split(' ').slice(0, -1).join(' '))}
                  <br />
                  {heroData.title_en && splitChars(heroData.title_en.split(' ').slice(-1)[0])}
                </h1>
              </div>
            </div>
            <div className="hero__text-box text-box" ref={textBoxRef}>
              <p className="h-text">{heroData.description_en}</p>
            </div>
          </div>

          <div className="hero__info-strip">
            {exhibitionInfo.map((item, i) => (
              <React.Fragment key={item.id}>
                {i !== 0 && <div className="hero__info-divider" />}
                <div className="hero__info-item" ref={stripRefs[i]}>
                  <img src={iconMap[item.title]} alt={item.title} className="h-sml-img" />
                  <span className="h-sml-title">{item.title}</span>
                  <span className="h-sml-text">
                    {item.info_text_line1}<br />{item.info_text_line2}
                  </span>
                </div>
              </React.Fragment>
            ))}
          </div>
        </section>

        <ScrollVelocity
          texts={['Discover the future of luxury interiors, ', 'innovative architecture, and smart living.']}
          velocity={100}
          className="scroller"
          numCopies={6}
          damping={50}
          stiffness={400}
        />

        <section className="about-sectionn" ref={aboutRef}>
          <div className="about__content">
            <div ref={aboutLabelRef}><Label labelt={about.tag_label_en} /></div>
            <h2 className="h-title1" ref={aboutTitleRef}>{about.headline_en}</h2>
            <p className="h-text1" ref={aboutTextRef}>{about.description_en}</p>
            <div className="about__stats">
              {aboutStats.map((item, i) => (
                <div className="about__stat-item" key={item.id} ref={statRefs[i]}>
                  <span className="h-num">{item.stat_count_en}</span>
                  <span className="h-num-label">{item.stat_label_en}</span>
                </div>
              ))}
            </div>
            <Btn2 btnt={about.read_more_cta_en} />
          </div>
          <div className="about__image-wrap h-img" ref={aboutImgRef}>
            <div className="about__image-inner" />
          </div>
        </section>

        <section className="talks-section" ref={talksRef}>
          <div className="talks-section__bg">
            <svg className="talks-svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1175 692" fill="none" preserveAspectRatio="none" aria-hidden="true">
              <path d="M609.985 0.000976562C622.992 0.00179558 635.983 0.00264279 648.935 0.00488281C648.934 0.83331 649.606 1.50571 650.435 1.50586C664.466 1.50835 678.449 1.51147 692.352 1.51562C693.18 1.51587 693.851 0.844052 693.852 0.015625C706.941 0.0195959 719.957 0.0248219 732.873 0.0302734C732.873 0.858551 733.544 1.5298 734.372 1.53027C748.508 1.53631 762.523 1.54395 776.38 1.55176C777.208 1.55198 777.878 0.880833 777.879 0.0527344C791.018 0.0602047 804.014 0.0680792 816.837 0.0771484C816.836 0.905568 817.508 1.57752 818.336 1.57812C832.526 1.58824 846.503 1.59925 860.224 1.61133C861.052 1.61206 861.724 0.941505 861.725 0.113281C874.919 0.12498 887.874 0.136872 900.555 0.150391C900.554 0.978817 901.224 1.65145 902.053 1.65234C916.402 1.66775 930.395 1.68441 943.979 1.70215C944.807 1.70308 945.478 1.03146 945.479 0.203125C958.891 0.220767 971.9 0.240811 984.453 0.260742C984.452 1.08897 985.123 1.76137 985.951 1.7627C1000.55 1.78605 1014.52 1.81143 1027.79 1.83789C1028.62 1.83954 1029.3 1.16907 1029.3 0.34082C1043.21 0.368797 1056.33 0.399239 1068.58 0.430664C1068.58 1.2585 1069.25 1.93134 1070.07 1.93359C1085.64 1.97396 1099.76 2.01649 1112.23 2.0625C1113.05 2.0655 1113.73 1.39787 1113.73 0.570312C1129.65 0.629985 1142.81 0.692652 1152.77 0.761719C1152.76 1.58999 1153.43 2.26646 1154.26 2.27246C1160.51 2.31757 1165.4 2.3641 1168.81 2.41309C1170.52 2.43759 1171.86 2.463 1172.81 2.48828C1172.9 2.49064 1172.98 2.49374 1173.06 2.49609C1173.06 3.40665 1173.07 4.74467 1173.07 6.48438C1173.07 10.0239 1173.08 15.2274 1173.09 21.8838C1173.09 22.7113 1173.77 23.3807 1174.59 23.3809C1174.61 33.5585 1174.63 46.8905 1174.65 62.6904C1173.83 62.6932 1173.16 63.3649 1173.16 64.1924C1173.18 76.8715 1173.2 91.096 1173.22 106.521C1173.22 107.348 1173.89 108.018 1174.72 108.018C1174.73 120.409 1174.75 133.561 1174.77 147.297C1173.94 147.3 1173.27 147.971 1173.27 148.799C1173.29 162.404 1173.31 176.573 1173.33 191.135C1173.33 191.963 1174 192.635 1174.83 192.634C1174.84 205.462 1174.86 218.591 1174.87 231.904C1174.04 231.905 1173.37 232.577 1173.37 233.405C1173.39 247.345 1173.4 261.481 1173.41 275.684C1173.41 276.512 1174.09 277.182 1174.91 277.182C1174.92 290.256 1174.94 303.381 1174.95 316.455C1174.12 316.457 1173.45 317.128 1173.45 317.956C1173.46 332.168 1173.47 346.314 1173.48 360.263C1173.48 361.091 1174.15 361.76 1174.97 361.761C1174.98 375.05 1174.99 388.156 1174.99 400.964C1174.16 400.964 1173.49 401.636 1173.49 402.465C1173.5 417.033 1173.5 431.208 1173.5 444.819C1173.5 445.647 1174.17 446.317 1175 446.318C1174.99 460.071 1174.99 473.239 1174.98 485.646C1174.15 485.648 1173.48 486.319 1173.48 487.146C1173.47 502.546 1173.46 516.75 1173.44 529.416C1173.44 530.244 1174.11 530.916 1174.93 530.918C1174.91 546.73 1174.87 560.077 1174.82 570.271C1173.99 570.269 1173.32 570.938 1173.31 571.765C1173.28 578.017 1173.25 582.989 1173.21 586.51C1173.18 588.566 1173.15 590.088 1173.13 591.078H1160.89C1160.06 591.078 1159.39 591.75 1159.39 592.578H1135.03C1135.03 591.75 1134.36 591.078 1133.53 591.078H1119.86C1116.51 591.078 1113.26 591.687 1110.17 592.83L1109.56 593.065C1108.79 593.371 1108.42 594.244 1108.72 595.014C1108.73 595.026 1108.73 595.038 1108.74 595.051C1103.81 597.323 1099.42 601.076 1096 605.96C1095.99 605.954 1095.99 605.948 1095.98 605.942C1095.32 605.523 1094.47 605.676 1094 606.271L1093.91 606.396C1092.22 609.032 1090.8 611.947 1089.69 615.095L1089.48 615.728L1089.47 615.754L1089.46 615.781C1088.13 620.355 1086.89 624.953 1085.69 629.486C1084.48 634.025 1083.29 638.496 1082.05 642.842C1081.82 643.636 1082.28 644.465 1083.07 644.695C1077.49 663.79 1070.48 680.314 1054.92 687.771C1054.58 687.031 1053.71 686.693 1052.96 687.017C1046.05 690.002 1037.34 691.241 1026.14 690.054L1026.06 690.045H1004.06C1003.23 690.045 1002.56 690.717 1002.56 691.545H961.703C961.703 690.717 961.031 690.045 960.203 690.045H916.35C915.521 690.045 914.85 690.717 914.85 691.545H873.996C873.996 690.717 873.324 690.045 872.496 690.045H828.643C827.814 690.045 827.143 690.717 827.143 691.545H786.289C786.289 690.717 785.617 690.045 784.789 690.045H740.936C740.107 690.045 739.436 690.717 739.436 691.545H698.582C698.582 690.717 697.91 690.045 697.082 690.045H653.229C652.4 690.045 651.729 690.717 651.729 691.545H610.875C610.875 690.717 610.203 690.045 609.375 690.045H565.521C564.693 690.045 564.022 690.717 564.021 691.545H523.169C523.169 690.717 522.497 690.045 521.669 690.045H477.815C476.987 690.045 476.316 690.717 476.315 691.545H435.462C435.462 690.717 434.79 690.045 433.962 690.045H390.108C389.28 690.045 388.609 690.717 388.608 691.545H347.755C347.755 690.717 347.083 690.045 346.255 690.045H302.401C301.573 690.045 300.902 690.717 300.901 691.545H260.048C260.048 690.717 259.376 690.045 258.548 690.045H214.694C213.866 690.045 213.195 690.717 213.194 691.545H172.341C172.341 690.717 171.669 690.045 170.841 690.045H148.991C148.967 690.042 148.938 690.04 148.904 690.036C148.794 690.024 148.63 690.007 148.418 689.983C147.994 689.937 147.375 689.869 146.601 689.78C145.051 689.603 142.876 689.346 140.378 689.025C139.558 688.92 138.807 689.499 138.699 690.318C134.168 689.722 128.84 688.945 124.272 688.076C124.43 687.268 123.908 686.483 123.1 686.319L121.693 686.024C120.331 685.728 119.102 685.426 118.061 685.123C116.637 684.709 115.702 684.33 115.233 684.029C107.839 679.075 102.98 670.949 99.2246 661.301L99.1621 661.16C98.8177 660.487 98.014 660.168 97.293 660.443C90.8147 643.093 87.8214 622.059 79.8047 608.051C79.8119 608.046 79.82 608.043 79.8271 608.039C80.5327 607.605 80.753 606.681 80.3193 605.976L79.8047 605.158C74.4175 596.801 66.8229 591.194 55.1582 591.078H41.4658C40.6376 591.078 39.966 591.75 39.9658 592.578H15.6113C15.6111 591.75 14.9395 591.078 14.1113 591.078H1.87109C1.84581 590.088 1.81803 588.567 1.79395 586.511C1.75271 582.99 1.71614 578.017 1.68457 571.765L1.67676 571.611C1.59636 570.856 0.955443 570.269 0.179688 570.271C0.130956 560.077 0.0919416 546.73 0.0644531 530.918C0.892744 530.916 1.56292 530.244 1.56152 529.416C1.5402 516.75 1.52463 502.545 1.51465 487.146C1.51386 486.318 0.843748 485.647 0.0166016 485.646C0.00887303 473.24 0.00245673 460.072 0.000976562 446.32C0.829213 446.32 1.50008 445.648 1.5 444.819L1.50488 402.465C1.50511 401.636 0.83331 400.964 0.00488281 400.964C0.00847961 388.156 0.0155108 375.05 0.0224609 361.761C0.798439 361.76 1.43646 361.172 1.51367 360.416L1.52148 360.263L1.54883 317.956C1.54944 317.128 0.878773 316.456 0.0507812 316.455C0.0604484 303.381 0.0703805 290.256 0.0820312 277.182C0.85861 277.182 1.49766 276.593 1.5752 275.837L1.58301 275.684C1.59574 261.481 1.60961 247.345 1.62402 233.405C1.62481 232.629 1.03546 231.99 0.279297 231.912L0.125977 231.903C0.139799 218.59 0.154207 205.462 0.168945 192.634C0.9456 192.635 1.58458 192.044 1.66211 191.288L1.6709 191.135L1.72168 148.799C1.72272 147.971 1.05302 147.299 0.225586 147.297C0.242939 133.561 0.259572 120.409 0.276367 108.017C1.05218 108.017 1.69054 107.429 1.76855 106.674L1.77734 106.521L1.83691 64.1924C1.83812 63.3647 1.16825 62.6928 0.34082 62.6904C0.363881 46.8905 0.383532 33.5585 0.399414 23.3809C1.17542 23.3813 1.81465 22.7929 1.89258 22.0371L1.90039 21.8838C1.91081 15.2274 1.91906 10.0239 1.9248 6.48438C1.92763 4.74469 1.92916 3.40664 1.93066 2.49609C2.01209 2.49374 2.09703 2.49064 2.18555 2.48828C3.13533 2.463 4.47107 2.43759 6.17969 2.41309C7.88752 2.38859 9.964 2.36532 12.3945 2.3418L20.7354 2.27246C21.5636 2.2664 22.2295 1.58994 22.2236 0.761719C32.1844 0.692653 45.342 0.629984 61.2617 0.570312C61.2658 1.39789 61.9398 2.06555 62.7676 2.0625C69.0015 2.0395 75.6479 2.01769 82.6816 1.99609L104.92 1.93359C105.748 1.93145 106.417 1.25855 106.415 0.430664C118.659 0.39924 131.784 0.368796 145.694 0.34082C145.696 1.16901 146.369 1.83947 147.197 1.83789L189.042 1.7627C189.87 1.76132 190.539 1.08893 190.538 0.260742C203.092 0.240811 216.101 0.220767 229.513 0.203125V0.204102C229.514 1.03245 230.186 1.70323 231.015 1.70215L272.941 1.65234C273.77 1.65124 274.439 0.978684 274.438 0.150391C287.119 0.136873 300.074 0.124979 313.268 0.113281C313.269 0.941499 313.941 1.61206 314.77 1.61133C328.49 1.59925 342.467 1.58824 356.657 1.57812C357.486 1.57753 358.156 0.905575 358.155 0.0771484C370.978 0.0680799 383.974 0.060204 397.113 0.0527344C397.114 0.880829 397.785 1.55198 398.613 1.55176L440.621 1.53027C441.449 1.52977 442.119 0.858528 442.119 0.0302734C455.035 0.0248226 468.052 0.0195952 481.141 0.015625C481.141 0.844052 481.813 1.51587 482.642 1.51562L524.559 1.50586C525.387 1.50571 526.058 0.83331 526.058 0.00488281C539.009 0.00264351 552.001 0.00179491 565.007 0.000976562C565.007 0.829188 565.679 1.50088 566.507 1.50098C580.495 1.50016 594.499 1.50015 608.486 1.50098C609.315 1.50092 609.985 0.829217 609.985 0.000976562Z" fill="var(--color-bg-darker)" stroke="var(--color-primary)" strokeWidth="3" strokeLinecap="round" strokeDasharray="42 42" />
            </svg>
            <div className="talks-section__inner">
              <h2 className="card-title" ref={talksTitleRef}>DESIGN TALKS</h2>
              <p className="card-subtext">Learn from industry leaders and thought pioneers</p>
              <div className="talks-cards">
                {talks.map((item, i) => (
                  <div key={item.id} ref={cardRefs[i]} className="talks-card-wrapper">
                    <TalkCard image={item.img} time={item.talk_time_en} speaker={item.speaker_name_en} title={item.topic_en} />
                  </div>
                ))}
              </div>
              <p className="talks-more">+ 47 MORE TALKS SCHEDULED</p>
            </div>
          </div>
        </section>

        <section className="ticket-section" ref={ticketRef}>
          <div className="ticket__image-side">
            <img src={ticketImg} alt="Ticket" className="ticket-img" ref={ticketImgRef} />
          </div>
          <div className="ticket__content">
            <Label labelt={ticket.main_tag_en} />
            <h2 className="h-title1" ref={ticketTitleRef}>{ticket.headline_en}</h2>
            <p className="h-text1">{ticket.description_en}</p>
            <div className="ticket__points">
              {ticketPoints.map((item, i) => (
                <div className="ticket__point-item" key={item.id} ref={pointRefs[i]}>
                  <div className="ticket__point-icon">{pointIcons[i]}</div>
                  <div>
                    <p className="points-title">{item.point_title_en}</p>
                    <p className="points-text">{item.point_desc_en}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="ticket__btns" ref={ticketBtnsRef}>
              <Link to="/book" className="no-underline-link">
                <Btn2 btnt={ticket.book_ticket_cta_en} />
              </Link>
              <Link to="/exhibtor" className="no-underline-link">
                <button className='btn4'>VIEW EXHIBITORS</button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default Home;